import React, { useState, useEffect } from 'react';
import { strategyService } from '../../services/strategyService';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

type Step = 
  | 'CAT_INIT'
  | 'CAT_MANUAL'
  | 'CAT_UPLOAD'
  | 'CAT_REVIEW'
  | 'BBS_INIT'
  | 'BBS_MANUAL'
  | 'BBS_UPLOAD'
  | 'BBS_REVIEW'
  | 'OP_REVIEW'
  | 'TARGET_INIT'
  | 'TARGET_UPLOAD'
  | 'TARGET_REVIEW'
  | 'INSIGHT_GENERATE'
  | 'INSIGHT_REVIEW'
  | 'COMPLETED';

const Module2Insight: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [step, setStep] = useState<Step>('CAT_INIT');
  const [loading, setLoading] = useState(false);
  
  // Forms & Temp Data
  const [inputText, setInputText] = useState('');
  
  // Accumulator
  const [stateData, setStateData] = useState<any>({});

  // Chat state for CAT iteration
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<{ role: string; content: string }[]>([]);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, loading]);

  useEffect(() => {
    setChatHistory([]);
    setChatMessage('');
  }, [step]);

  useEffect(() => {
    loadStrategy();
  }, [strategyId]);

  const loadStrategy = async () => {
    if (!strategyId) return;
    try {
      const data = await strategyService.getById(strategyId);
      if (data.module_2_insight) {
        let parsed = typeof data.module_2_insight === 'string' ? JSON.parse(data.module_2_insight) : data.module_2_insight;
        setStateData(parsed || {});
        // Determine step based on what's missing, or just start from INIT if not completed
        if (data.status === 'Módulo 2 Completado') {
          setStep('COMPLETED');
        } else if (parsed.insight) {
          setStep('INSIGHT_REVIEW');
        } else if (parsed.target_psicografia) {
          setStep(parsed.insight_predefinido ? 'COMPLETED' : 'INSIGHT_GENERATE');
        } else if (parsed.op) {
          setStep('TARGET_INIT');
        } else if (parsed.bbs) {
          setStep('OP_REVIEW'); // Next is OP generation, but we jump to OP_REVIEW and hit generate if not exist
        } else if (parsed.cat) {
          setStep('BBS_INIT');
        } else {
          setStep('CAT_INIT');
        }
      } else {
        setStep('CAT_INIT');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const saveManualInsight = async () => {
    if (!inputText) return;
    setLoading(true);
    try {
      await api.post(`/strategies/${strategyId}/manual-insight`, { insight: inputText });
      await loadStrategy();
      setStep('CAT_INIT');
      setInputText('');
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_Modulo_II.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar PDF: ' + err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setLoading(true);
    let combinedText = '';

    try {
      // Subir los archivos de uno en uno para evitar el error 413 (Payload Too Large)
      // que ocurre en Nginx/Cloudflare al subir muchos videos pesados a la vez.
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const formData = new FormData();
        formData.append('context_files', file);

        const res = await api.post(`/strategies/${strategyId}/modules/2/upload_context`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (res.data.text) {
          combinedText += (combinedText ? '\n\n---\n\n' : '') + res.data.text;
        }
      }

      setInputText(prev => prev ? prev + '\n\n---\n\n' + combinedText : combinedText);
    } catch (err) {
      alert('Error al subir archivo: ' + err);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const sendChat = async () => {
    if (!chatMessage || loading) return;
    setLoading(true);
    setChatHistory(prev => [...prev, { role: 'user', content: chatMessage }]);
    const currentMsg = chatMessage;
    setChatMessage('');
    
    const currentAgentStep = step === 'BBS_REVIEW' ? 'bbs' : step === 'OP_REVIEW' ? 'op' : step === 'INSIGHT_REVIEW' ? 'insight' : 'cat';
    
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/2/chat`, { 
        message: currentMsg,
        agent_step: currentAgentStep
      });
      
      const result = parseIterationResponse<any>(res.data);
      const state = result.state || {};

      if (Object.keys(state).length > 0) {
        setStateData((prev: any) => ({ ...prev, ...state }));
      }

      const replyContent = result.reply;
      if (replyContent && replyContent.trim() !== '') {
        setChatHistory(prev => [...prev, { role: 'assistant', content: replyContent }]);
      }
    } catch (err) {
      console.error(err);
      alert('Error en el chat: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const runAgent = async (agentStep: string, agentData: string = '') => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/2/process`, {
        agent_step: agentStep,
        agent_data: agentData
      });
      let parsed = typeof res.data.module_2_insight === 'string' ? JSON.parse(res.data.module_2_insight) : res.data.module_2_insight;
      setStateData(parsed || {});
      return parsed;
    } catch (err) {
      alert('Error: ' + err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = async () => {
    // Determine action based on current step
    switch (step) {

      case 'CAT_MANUAL':
      case 'CAT_UPLOAD':
        if (!(await runAgent('cat', inputText))) return;
        setInputText('');
        setStep('CAT_REVIEW');
        break;
      case 'CAT_REVIEW':
        setStep('BBS_INIT');
        break;
      case 'BBS_MANUAL':
      case 'BBS_UPLOAD':
        if (!(await runAgent('bbs', inputText))) return;
        setInputText('');
        setStep('BBS_REVIEW');
        break;
      case 'BBS_REVIEW':
        // Auto-run OP immediately after BBS is validated
        if (!(await runAgent('op'))) return;
        setStep('OP_REVIEW');
        break;
      case 'OP_REVIEW':
        setStep('TARGET_INIT');
        break;
      case 'TARGET_UPLOAD':
        // Run Target agent with or without input text
        if (!(await runAgent('target', inputText))) return;
        setInputText('');
        setStep('TARGET_REVIEW');
        break;
      case 'TARGET_REVIEW':
        if (stateData.insight_predefinido) {
          // If we had a pre-defined insight, we skip INSIGHT agent and complete
          if (!(await runAgent('complete'))) return;
          setStep('COMPLETED');
        } else {
          setStep('INSIGHT_GENERATE');
        }
        break;
      case 'INSIGHT_GENERATE':
        if (!(await runAgent('insight'))) return;
        setStep('INSIGHT_REVIEW');
        break;
      case 'INSIGHT_REVIEW':
        if (!(await runAgent('complete'))) return;
        setStep('COMPLETED');
        break;
      case 'COMPLETED':
        onComplete();
        break;
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'CAT_INIT':
        return (
          <div style={styles.card}>
            <h3>Paso 1: Agente CAT (Categoría)</h3>
            <p>¿Sabes cuál es la verdad de la categoría (océano rojo, qué dicen todos)?</p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <button style={styles.primaryButton} onClick={() => setStep('CAT_MANUAL')}>Sí, lo sé</button>
              <button style={styles.secondaryButton} onClick={() => setStep('CAT_UPLOAD')}>No, subir info</button>
            </div>
          </div>
        );

      case 'CAT_MANUAL':
      case 'CAT_UPLOAD':
        return (
          <div style={styles.card}>
            <h3>{step === 'CAT_MANUAL' ? 'Redacta la Verdad de la Categoría' : 'Sube la Información de la Categoría'}</h3>
            {step === 'CAT_UPLOAD' && (
               <input type="file" multiple style={styles.fileInput} accept=".pdf,.doc,.docx,.txt,image/*,video/*" onChange={handleFileUpload} disabled={loading} />
            )}
            <textarea style={styles.textarea} rows={4} value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Escribe o pega la información..." />
            <button style={styles.primaryButton} onClick={handleNextStep} disabled={loading || !inputText}>
              {loading ? 'Procesando (puede tardar hasta 60s en multimedia)...' : 'Subir y Analizar'}
            </button>
          </div>
        );
      
      case 'CAT_REVIEW':
        return (
          <div style={styles.card}>
            <h3>Revisión de CAT</h3>
            <p>El agente CAT ha redactado lo siguiente. Puedes editarlo antes de continuar.</p>
            <textarea style={styles.textarea} rows={4} value={stateData.cat || ''} onChange={e => setStateData({...stateData, cat: e.target.value})} />
            
            <div style={styles.chatSection}>
              <h3 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Iterar vía chat ("¿qué te hace ruido?")</h3>
              <div style={styles.chatHistory}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ ...styles.chatBubble, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? 'var(--bg-hover)' : 'var(--accent)', color: msg.role === 'user' ? 'var(--text-primary)' : '#FFF' }}>
                    {msg.content}
                  </div>
                ))}
                {loading && (
                  <div style={{ ...styles.chatBubble, alignSelf: 'flex-start', background: 'var(--accent)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <style>
                      {`
                        @keyframes typing {
                          0%, 100% { transform: translateY(0); opacity: 0.5; }
                          50% { transform: translateY(-3px); opacity: 1; }
                        }
                        .dot {
                          width: 6px;
                          height: 6px;
                          background-color: #FFF;
                          border-radius: 50%;
                          animation: typing 1.4s infinite ease-in-out;
                        }
                        .dot:nth-child(1) { animation-delay: 0s; }
                        .dot:nth-child(2) { animation-delay: 0.2s; }
                        .dot:nth-child(3) { animation-delay: 0.4s; }
                      `}
                    </style>
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatInputRow}>
                <input style={styles.chatInput} value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Escribe tus comentarios aquí..." onKeyDown={e => e.key === 'Enter' && !loading && sendChat()} disabled={loading} />
                <button style={styles.chatButton} onClick={sendChat} disabled={loading}>{loading ? '...' : 'Enviar'}</button>
              </div>
            </div>

            <button style={styles.successButton} onClick={handleNextStep} disabled={loading}>Validar y Continuar</button>
          </div>
        );

      case 'BBS_INIT':
        return (
          <div style={styles.card}>
            <h3>Paso 2: Agente BBS (Brand Best Self)</h3>
            <p>¿Sabes cuál es el "Brand Best Self" de la marca?</p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <button style={styles.primaryButton} onClick={() => setStep('BBS_MANUAL')}>Sí, lo sé</button>
              <button style={styles.secondaryButton} onClick={() => setStep('BBS_UPLOAD')}>No, subir info</button>
            </div>
          </div>
        );

      case 'BBS_MANUAL':
      case 'BBS_UPLOAD':
        return (
          <div style={styles.card}>
            <h3>{step === 'BBS_MANUAL' ? 'Redacta el Brand Best Self' : 'Sube la Información de la Marca'}</h3>
            {step === 'BBS_UPLOAD' && (
               <input type="file" multiple style={styles.fileInput} accept=".pdf,.doc,.docx,.txt,image/*,video/*" onChange={handleFileUpload} disabled={loading} />
            )}
            <textarea style={styles.textarea} rows={4} value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Escribe o pega la información..." />
            <button style={styles.primaryButton} onClick={handleNextStep} disabled={loading || !inputText}>
              {loading ? 'Procesando (puede tardar hasta 60s en multimedia)...' : 'Subir y Analizar'}
            </button>
          </div>
        );
      
      case 'BBS_REVIEW':
        return (
          <div style={styles.card}>
            <h3>Revisión de BBS</h3>
            <p>El agente BBS ha redactado lo siguiente. Puedes editarlo antes de continuar.</p>
            <textarea style={styles.textarea} rows={4} value={stateData.bbs || ''} onChange={e => setStateData({...stateData, bbs: e.target.value})} />
            
            <div style={styles.chatSection}>
              <h3 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Iterar vía chat ("¿qué te hace ruido?")</h3>
              <div style={styles.chatHistory}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ ...styles.chatBubble, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? 'var(--bg-hover)' : 'var(--accent)', color: msg.role === 'user' ? 'var(--text-primary)' : '#FFF' }}>
                    {msg.content}
                  </div>
                ))}
                {loading && (
                  <div style={{ ...styles.chatBubble, alignSelf: 'flex-start', background: 'var(--accent)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <style>
                      {`
                        @keyframes typing {
                          0%, 100% { transform: translateY(0); opacity: 0.5; }
                          50% { transform: translateY(-3px); opacity: 1; }
                        }
                        .dot {
                          width: 6px;
                          height: 6px;
                          background-color: #FFF;
                          border-radius: 50%;
                          animation: typing 1.4s infinite ease-in-out;
                        }
                        .dot:nth-child(1) { animation-delay: 0s; }
                        .dot:nth-child(2) { animation-delay: 0.2s; }
                        .dot:nth-child(3) { animation-delay: 0.4s; }
                      `}
                    </style>
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatInputRow}>
                <input style={styles.chatInput} value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Escribe tus comentarios aquí..." onKeyDown={e => e.key === 'Enter' && !loading && sendChat()} disabled={loading} />
                <button style={styles.chatButton} onClick={sendChat} disabled={loading}>{loading ? '...' : 'Enviar'}</button>
              </div>
            </div>

            <button style={styles.successButton} onClick={handleNextStep} disabled={loading}>{loading ? 'Procesando...' : 'Validar y Generar OP'}</button>
          </div>
        );

      case 'OP_REVIEW':
        return (
          <div style={styles.card}>
            <h3>Paso 3: Agente OP (Oportunidad Estratégica)</h3>
            <p>El agente OP ha cruzado CAT y BBS para generar la Oportunidad Estratégica.</p>
            <textarea style={styles.textarea} rows={4} value={stateData.op || ''} onChange={e => setStateData({...stateData, op: e.target.value})} />
            
            <div style={styles.chatSection}>
              <h3 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Iterar vía chat ("¿qué te hace ruido?")</h3>
              <div style={styles.chatHistory}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ ...styles.chatBubble, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? 'var(--bg-hover)' : 'var(--accent)', color: msg.role === 'user' ? 'var(--text-primary)' : '#FFF' }}>
                    {msg.content}
                  </div>
                ))}
                {loading && (
                  <div style={{ ...styles.chatBubble, alignSelf: 'flex-start', background: 'var(--accent)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <style>
                      {`
                        @keyframes typing {
                          0%, 100% { transform: translateY(0); opacity: 0.5; }
                          50% { transform: translateY(-3px); opacity: 1; }
                        }
                        .dot {
                          width: 6px;
                          height: 6px;
                          background-color: #FFF;
                          border-radius: 50%;
                          animation: typing 1.4s infinite ease-in-out;
                        }
                        .dot:nth-child(1) { animation-delay: 0s; }
                        .dot:nth-child(2) { animation-delay: 0.2s; }
                        .dot:nth-child(3) { animation-delay: 0.4s; }
                      `}
                    </style>
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatInputRow}>
                <input style={styles.chatInput} value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Escribe tus comentarios aquí..." onKeyDown={e => e.key === 'Enter' && !loading && sendChat()} disabled={loading} />
                <button style={styles.chatButton} onClick={sendChat} disabled={loading}>{loading ? '...' : 'Enviar'}</button>
              </div>
            </div>

            <button style={styles.successButton} onClick={handleNextStep} disabled={loading}>Validar y Continuar</button>
          </div>
        );

      case 'TARGET_INIT':
        return (
          <div style={styles.card}>
            <h3>Paso 4: Agente TARGET (Buyer Persona)</h3>
            <p>¿Tienes información del consumidor / target?</p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <button style={styles.primaryButton} onClick={() => setStep('TARGET_UPLOAD')}>Sí, la tengo</button>
              <button style={styles.secondaryButton} disabled={loading} onClick={async () => {
                await runAgent('target', 'Investigar internet para el target basado en el brief y la categoría.');
                setStep('TARGET_REVIEW');
              }}>{loading ? 'Procesando e Investigando Internet...' : 'No (Buscar en internet)'}</button>
            </div>
          </div>
        );

      case 'TARGET_UPLOAD':
        return (
          <div style={styles.card}>
            <h3>Sube la información del Target</h3>
            <input type="file" multiple style={styles.fileInput} accept=".pdf,.doc,.docx,.txt,image/*,video/*" onChange={handleFileUpload} disabled={loading} />
            <textarea style={styles.textarea} rows={4} value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Escribe o pega la información..." />
            <button style={styles.primaryButton} onClick={handleNextStep} disabled={loading || !inputText}>
              {loading ? 'Generando Target...' : 'Generar Buyer Persona'}
            </button>
          </div>
        );

      case 'TARGET_REVIEW':
        return (
          <div style={styles.card}>
            <h3>Revisión del TARGET (Buyer Persona)</h3>
            <p>El agente TARGET ha generado el perfil. Por favor, revísalo y ajústalo si es necesario:</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px', marginTop: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Nombre Ficticio</label>
                <input 
                  type="text" 
                  style={{ ...styles.chatInput, width: '100%', marginTop: '6px' }} 
                  value={stateData.target_name || ''} 
                  onChange={e => setStateData({...stateData, target_name: e.target.value})} 
                  placeholder="Ej: Sofia..."
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Edad</label>
                <input 
                  type="text" 
                  style={{ ...styles.chatInput, width: '100%', marginTop: '6px' }} 
                  value={stateData.target_age || ''} 
                  onChange={e => setStateData({...stateData, target_age: e.target.value})} 
                  placeholder="Ej: 25-35 años..."
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Ocupación</label>
                <input 
                  type="text" 
                  style={{ ...styles.chatInput, width: '100%', marginTop: '6px' }} 
                  value={stateData.target_occupation || ''} 
                  onChange={e => setStateData({...stateData, target_occupation: e.target.value})} 
                  placeholder="Ej: Profesional..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Contexto de Vida</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_life_context || ''} 
                  onChange={e => setStateData({...stateData, target_life_context: e.target.value})} 
                  placeholder="Cómo es su vida, rutina, aspiraciones..."
                />
              </div>
              
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Tensiones y Barreras Latentes</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_barriers || ''} 
                  onChange={e => setStateData({...stateData, target_barriers: e.target.value})} 
                  placeholder="Frustraciones, miedos, tensiones..."
                />
              </div>
              
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Passion Points (Intereses principales)</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_passion_points || ''} 
                  onChange={e => setStateData({...stateData, target_passion_points: e.target.value})} 
                  placeholder="Pasiones, hobbies, aficiones..."
                />
              </div>
              
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Key Touch Points (Canales de afinidad)</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_key_touchpoints || ''} 
                  onChange={e => setStateData({...stateData, target_key_touchpoints: e.target.value})} 
                  placeholder="Puntos de contacto, medios que consume..."
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Sección A: Psicografía (Resumen)</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_psicografia || ''} 
                  onChange={e => setStateData({...stateData, target_psicografia: e.target.value})} 
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Sección B: Demografía (Resumen)</label>
                <textarea 
                  style={styles.textarea} 
                  rows={3} 
                  value={stateData.target_demografia || ''} 
                  onChange={e => setStateData({...stateData, target_demografia: e.target.value})} 
                />
              </div>
            </div>
            
            <button style={styles.successButton} onClick={handleNextStep} disabled={loading}>Validar y Continuar</button>
          </div>
        );

      case 'INSIGHT_GENERATE':
        return (
          <div style={styles.card}>
            <h3>Paso 5: Agente INSIGHT</h3>
            <p>Es el momento de generar el Insight basado en OP y TARGET.</p>
            <button style={styles.primaryButton} onClick={handleNextStep} disabled={loading}>
              {loading ? 'Generando...' : 'Pulsa aquí para generar el INSIGHT'}
            </button>
          </div>
        );

      case 'INSIGHT_REVIEW':
        return (
          <div style={styles.card}>
            <h3>Revisión del INSIGHT</h3>
            <p>El agente IN ha redactado el siguiente insight:</p>
            <textarea style={{...styles.textarea, fontSize: '16px', fontWeight: 'bold'}} rows={4} value={stateData.insight || stateData.insight_predefinido || ''} onChange={e => setStateData({...stateData, insight: e.target.value})} />
            
            <div style={styles.chatSection}>
              <h3 style={{ fontSize: '14px', marginBottom: '16px', color: 'var(--text-primary)' }}>Iterar vía chat ("¿qué te hace ruido?")</h3>
              <div style={styles.chatHistory}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ ...styles.chatBubble, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? 'var(--bg-hover)' : 'var(--accent)', color: msg.role === 'user' ? 'var(--text-primary)' : '#FFF' }}>
                    {msg.content}
                  </div>
                ))}
                {loading && (
                  <div style={{ ...styles.chatBubble, alignSelf: 'flex-start', background: 'var(--accent)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <style>
                      {`
                        @keyframes typing {
                          0%, 100% { transform: translateY(0); opacity: 0.5; }
                          50% { transform: translateY(-3px); opacity: 1; }
                        }
                        .dot {
                          width: 6px;
                          height: 6px;
                          background-color: #FFF;
                          border-radius: 50%;
                          animation: typing 1.4s infinite ease-in-out;
                        }
                        .dot:nth-child(1) { animation-delay: 0s; }
                        .dot:nth-child(2) { animation-delay: 0.2s; }
                        .dot:nth-child(3) { animation-delay: 0.4s; }
                      `}
                    </style>
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatInputRow}>
                <input style={styles.chatInput} value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Escribe tus comentarios aquí..." onKeyDown={e => e.key === 'Enter' && !loading && sendChat()} disabled={loading} />
                <button style={styles.chatButton} onClick={sendChat} disabled={loading}>{loading ? '...' : 'Enviar'}</button>
              </div>
            </div>

            <button style={styles.successButton} onClick={handleNextStep} disabled={loading}>Validar y Continuar</button>
          </div>
        );

      case 'COMPLETED':
        return (
          <>
            <div style={styles.resultGrid}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>CAT (Categoría)</h3>
                <p style={styles.cardContent}>{stateData.cat}</p>
              </div>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>BBS (Brand Best Self)</h3>
                <p style={styles.cardContent}>{stateData.bbs}</p>
              </div>
              <div style={{...styles.card, gridColumn: '1 / -1'}}>
                <h3 style={styles.cardTitle}>OP (Oportunidad)</h3>
                <p style={styles.cardContent}>{stateData.op}</p>
              </div>

              {stateData.target_name ? (
                <div style={{...styles.card, gridColumn: '1 / -1', borderLeft: '4px solid var(--accent)'}}>
                  <h3 style={{...styles.cardTitle, color: 'var(--accent)'}}>Buyer Persona: {stateData.target_name}</h3>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    <strong>Edad:</strong> {stateData.target_age} | <strong>Ocupación:</strong> {stateData.target_occupation}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{background: 'var(--bg-hover)', padding: '16px', borderRadius: '6px'}}>
                      <strong style={{fontSize: '11px', color: 'var(--text-muted)'}}>CONTEXTO DE VIDA</strong>
                      <p style={{margin: '8px 0 0 0', fontSize: '13px', lineHeight: '1.5'}}>{stateData.target_life_context}</p>
                    </div>
                    <div style={{background: 'var(--bg-hover)', padding: '16px', borderRadius: '6px'}}>
                      <strong style={{fontSize: '11px', color: 'var(--text-muted)'}}>TENSIONES Y BARRERAS</strong>
                      <p style={{margin: '8px 0 0 0', fontSize: '13px', lineHeight: '1.5'}}>{stateData.target_barriers}</p>
                    </div>
                    <div style={{background: 'var(--bg-hover)', padding: '16px', borderRadius: '6px'}}>
                      <strong style={{fontSize: '11px', color: 'var(--text-muted)'}}>PASSION POINTS</strong>
                      <p style={{margin: '8px 0 0 0', fontSize: '13px', lineHeight: '1.5'}}>{stateData.target_passion_points}</p>
                    </div>
                    <div style={{background: 'var(--bg-hover)', padding: '16px', borderRadius: '6px'}}>
                      <strong style={{fontSize: '11px', color: 'var(--text-muted)'}}>KEY TOUCH POINTS (CANALES AFINES)</strong>
                      <p style={{margin: '8px 0 0 0', fontSize: '13px', lineHeight: '1.5'}}>{stateData.target_key_touchpoints}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Sección A: Psicografía</h3>
                    <p style={styles.cardContent}>{stateData.target_psicografia}</p>
                  </div>
                  <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Sección B: Demografía</h3>
                    <p style={styles.cardContent}>{stateData.target_demografia}</p>
                  </div>
                </>
              )}

              <div style={{ ...styles.card, gridColumn: '1 / -1', background: 'var(--bg-hover)', color: 'var(--text-primary)' }}>
                <h3 style={{ ...styles.cardTitle, color: 'var(--accent)' }}>El Insight (Tensión Humana)</h3>
                <p style={{ ...styles.cardContent, fontSize: '18px', fontWeight: 600 }}>"{stateData.insight || stateData.insight_predefinido}"</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button style={{ ...styles.secondaryButton, color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={handleExportPDF}>
                Exportar PDF
              </button>
              <button style={styles.successButton} onClick={onComplete}>Siguiente Módulo</button>
            </div>
          </>
        );

      default:
        return <div>Estado desconocido</div>;
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo II: El Insight</h2>
      <p style={styles.description}>Construye el Insight estratégico a través de agentes en cascada (CAT, BBS, OP, TARGET, IN).</p>
      
      {stateData?.insight_predefinido && step !== 'COMPLETED' && (
         <div style={{...styles.card, marginBottom: '24px', borderColor: 'var(--accent)', padding: '12px'}}>
           <h3 style={{fontSize: '12px', color: 'var(--accent)', marginBottom: '8px'}}>Insight Pre-definido por Usuario</h3>
           <p style={{fontSize: '14px', fontStyle: 'italic', margin: 0}}>"{stateData.insight_predefinido}"</p>
         </div>
      )}

      {renderContent()}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  fileInput: { display: 'block', padding: '8px', color: 'var(--text-primary)', marginBottom: '16px' },
  textarea: { width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', background: 'transparent', color: 'var(--text-primary)', marginTop: '8px', marginBottom: '16px' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '16px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  secondaryButton: { background: 'var(--bg-card)', color: 'var(--text-primary)', padding: '16px 24px', border: '1px solid var(--border)', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  successButton: { background: 'var(--success)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', marginTop: '16px' },
  resultGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  card: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-card)', color: 'var(--text-primary)' },
  cardTitle: { fontSize: '14px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '12px', letterSpacing: '0.05em' },
  cardContent: { fontSize: '14px', margin: 0, lineHeight: 1.6, color: 'var(--text-primary)' },
  chatSection: { border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', color: 'var(--text-primary)', boxSizing: 'border-box', maxWidth: '100%', marginBottom: '16px' },
  chatHistory: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', marginBottom: '16px', boxSizing: 'border-box', maxWidth: '100%' },
  chatBubble: { padding: '12px 16px', borderRadius: '16px', maxWidth: '80%', fontSize: '14px', color: 'var(--text-primary)', boxSizing: 'border-box', wordBreak: 'break-word', marginBottom: '4px' },
  chatInputRow: { display: 'flex', gap: '8px', width: '100%', boxSizing: 'border-box', maxWidth: '100%' },
  chatInput: { flex: 1, padding: '10px 16px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', color: 'var(--text-primary)', background: 'transparent', boxSizing: 'border-box', minWidth: 0 },
  chatButton: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, boxSizing: 'border-box' }
};

export default Module2Insight;
